import { useRef, useEffect, useState, useCallback } from "react";

/**
 * Hook personnalisé pour ajouter la fonctionnalité de drag scroll à un conteneur
 * Permet de faire défiler le contenu en utilisant le drag & drop de la souris
 * Inclut un effet d'inertie avec accélération et décélération
 */
const useDragScroll = () => {
  const ref = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ left: 0, top: 0 });

  // Variables pour l'inertie
  const velocity = useRef({ x: 0, y: 0 });
  const lastMove = useRef({ x: 0, y: 0, time: 0 });
  const animationFrame = useRef(null);
  const isDecelerating = useRef(false);

  // Constantes pour l'inertie (ajustées pour plus d'inertie)
  const FRICTION = 0.97; // Facteur de décélération (0-1) - plus élevé = plus d'inertie
  const MIN_VELOCITY = 0.05; // Vitesse minimale avant arrêt - plus bas = inertie plus longue
  const VELOCITY_SCALE = 5.5; // Facteur d'échelle pour la vitesse - plus élevé = plus de vitesse initiale

  // Fonction d'animation pour l'inertie (récursive)
  const animateInertia = useCallback(
    function animate() {
      const element = ref.current;
      if (!element || !isDecelerating.current) {
        return;
      }

      // Appliquer la friction
      velocity.current.x *= FRICTION;
      velocity.current.y *= FRICTION;

      // Vérifier si la vitesse est assez faible pour arrêter
      if (
        Math.abs(velocity.current.x) < MIN_VELOCITY &&
        Math.abs(velocity.current.y) < MIN_VELOCITY
      ) {
        isDecelerating.current = false;
        velocity.current = { x: 0, y: 0 };
        return;
      }

      // Appliquer le mouvement
      element.scrollLeft += velocity.current.x;
      element.scrollTop += velocity.current.y;

      // Continuer l'animation en appelant récursivement
      animationFrame.current = requestAnimationFrame(animate);
    },
    [FRICTION, MIN_VELOCITY]
  );

  // Arrêter l'animation d'inertie
  const stopInertia = useCallback(() => {
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
      animationFrame.current = null;
    }
    isDecelerating.current = false;
    velocity.current = { x: 0, y: 0 };
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleMouseDown = (e) => {
      // Ne pas déclencher le drag si on clique sur un élément interactif
      if (
        e.target.tagName === "BUTTON" ||
        e.target.tagName === "INPUT" ||
        e.target.tagName === "SELECT" ||
        e.target.tagName === "A" ||
        e.target.closest("button") ||
        e.target.closest("input") ||
        e.target.closest("select") ||
        e.target.closest("a") ||
        e.target.closest(".form-check") ||
        e.target.closest('[role="button"]')
      ) {
        return;
      }

      // Arrêter toute animation d'inertie en cours
      stopInertia();

      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      setScrollStart({
        left: element.scrollLeft,
        top: element.scrollTop,
      });

      // Réinitialiser les données de mouvement pour le calcul de vitesse
      lastMove.current = { x: e.clientX, y: e.clientY, time: Date.now() };
      velocity.current = { x: 0, y: 0 };

      // Empêcher la sélection de texte pendant le drag
      e.preventDefault();

      // Changer le curseur
      element.style.cursor = "grabbing";
      element.style.userSelect = "none";
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;

      e.preventDefault();

      const currentTime = Date.now();
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      // Calculer la vitesse en pixels par milliseconde
      const timeDelta = currentTime - lastMove.current.time;
      if (timeDelta > 0) {
        const moveDeltaX = e.clientX - lastMove.current.x;
        const moveDeltaY = e.clientY - lastMove.current.y;

        velocity.current.x = (moveDeltaX / timeDelta) * VELOCITY_SCALE;
        velocity.current.y = (moveDeltaY / timeDelta) * VELOCITY_SCALE;
      }

      // Mettre à jour les données de mouvement
      lastMove.current = { x: e.clientX, y: e.clientY, time: currentTime };

      // Calculer la nouvelle position de scroll
      const newScrollLeft = scrollStart.left - deltaX;
      const newScrollTop = scrollStart.top - deltaY;

      // Appliquer le scroll
      element.scrollLeft = newScrollLeft;
      element.scrollTop = newScrollTop;
    };

    const handleMouseUp = () => {
      if (!isDragging) return;

      setIsDragging(false);

      // Déclencher l'animation d'inertie si la vitesse est suffisante
      const totalVelocity = Math.sqrt(
        velocity.current.x ** 2 + velocity.current.y ** 2
      );

      if (totalVelocity > MIN_VELOCITY) {
        // Inverser la vitesse car le scroll va dans la direction opposée
        velocity.current.x = -velocity.current.x;
        velocity.current.y = -velocity.current.y;

        isDecelerating.current = true;
        animationFrame.current = requestAnimationFrame(animateInertia);
      }

      // Restaurer le curseur
      element.style.cursor = "grab";
      element.style.userSelect = "auto";
    };

    const handleMouseLeave = () => {
      if (isDragging) {
        handleMouseUp();
      }
    };

    // Gestionnaires pour les événements tactiles (mobile)
    const handleTouchStart = (e) => {
      if (e.touches.length !== 1) return;

      // Arrêter toute animation d'inertie en cours
      stopInertia();

      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX, y: touch.clientY });
      setScrollStart({
        left: element.scrollLeft,
        top: element.scrollTop,
      });

      // Réinitialiser les données de mouvement pour le calcul de vitesse
      lastMove.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      };
      velocity.current = { x: 0, y: 0 };

      // Empêcher le scroll par défaut sur mobile
      e.preventDefault();
    };

    const handleTouchMove = (e) => {
      if (!isDragging || e.touches.length !== 1) return;

      e.preventDefault();

      const touch = e.touches[0];
      const currentTime = Date.now();
      const deltaX = touch.clientX - dragStart.x;
      const deltaY = touch.clientY - dragStart.y;

      // Calculer la vitesse en pixels par milliseconde
      const timeDelta = currentTime - lastMove.current.time;
      if (timeDelta > 0) {
        const moveDeltaX = touch.clientX - lastMove.current.x;
        const moveDeltaY = touch.clientY - lastMove.current.y;

        velocity.current.x = (moveDeltaX / timeDelta) * VELOCITY_SCALE;
        velocity.current.y = (moveDeltaY / timeDelta) * VELOCITY_SCALE;
      }

      // Mettre à jour les données de mouvement
      lastMove.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: currentTime,
      };

      const newScrollLeft = scrollStart.left - deltaX;
      const newScrollTop = scrollStart.top - deltaY;

      element.scrollLeft = newScrollLeft;
      element.scrollTop = newScrollTop;
    };

    const handleTouchEnd = () => {
      if (!isDragging) return;

      setIsDragging(false);

      // Déclencher l'animation d'inertie si la vitesse est suffisante
      const totalVelocity = Math.sqrt(
        velocity.current.x ** 2 + velocity.current.y ** 2
      );
      if (totalVelocity > MIN_VELOCITY) {
        // Inverser la vitesse car le scroll va dans la direction opposée
        velocity.current.x = -velocity.current.x;
        velocity.current.y = -velocity.current.y;

        isDecelerating.current = true;
        animationFrame.current = requestAnimationFrame(animateInertia);
      }
    };

    // Ajouter les event listeners pour souris et tactile
    element.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    element.addEventListener("mouseleave", handleMouseLeave);

    // Événements tactiles
    element.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    element.addEventListener("touchmove", handleTouchMove, { passive: false });
    element.addEventListener("touchend", handleTouchEnd);

    // Définir le curseur initial
    element.style.cursor = "grab";

    // Cleanup
    return () => {
      // Ne nettoyer l'inertie que si on démonte le composant, pas si isDragging change
      // Si isDragging change de true à false, on veut garder l'inertie

      element.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      element.removeEventListener("mouseleave", handleMouseLeave);

      element.removeEventListener("touchstart", handleTouchStart);
      element.removeEventListener("touchmove", handleTouchMove);
      element.removeEventListener("touchend", handleTouchEnd);

      if (element) {
        element.style.cursor = "auto";
        element.style.userSelect = "auto";
      }
    };
  }, [isDragging, dragStart, scrollStart, animateInertia, stopInertia]);

  // Cleanup séparé pour l'inertie au démontage du composant
  useEffect(() => {
    return () => {
      // Nettoyer l'animation d'inertie au démontage
      stopInertia();
    };
  }, [stopInertia]);

  return { ref, isDragging };
};

export default useDragScroll;
